import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AdminRole, WithdrawalStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { StatusBadge } from "@/components/status-badge";
import { Amount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import { MerchantCell } from "@/components/merchant-cell";
import { WITHDRAWAL_STATUS_COLOR } from "@/lib/status-colors";
import { useTableState, type TableSearch } from "@/lib/table-state";
import { cn, formatStatusLabel } from "@/lib/utils";
import { hasRole } from "@/lib/roles";
import { approveWithdrawal, getWithdrawal, listWithdrawals, rejectWithdrawal } from "@/api/withdrawals";
import { ApiError } from "@/api/client";
import { type ListWithdrawalsParams, type WithdrawalListItem } from "@/api/types";

// Feature 030 Task 9: global withdrawal review queue — every merchant's
// withdrawal requests, with approve/reject actions on a PENDING_REVIEW row.
// Mirrors _app.high-risk.tsx's list + detail-dialog + per-action
// confirmation-dialog shape, adapted for this endpoint's real server-side
// pagination/filters (unlike the AML queue's unpaginated endpoint) — so the
// list itself follows _app.refunds.tsx's real `useTableState` + `DataTable`
// precedent instead. Read (`GET`) is FINANCE_VIEWER OR CONFIG_MANAGER, write
// (`POST` approve/reject) is CONFIG_MANAGER-only — same split as
// _app.gas-station.tsx's `canManage` pattern, gating the action buttons
// only, not the whole page.
type WithdrawalFilters = {
  status?: WithdrawalStatus;
  merchantId?: string;
};

const DEFAULT_FILTERS: WithdrawalFilters = {};

/** Plain type-guarded passthrough — mirrors _app.refunds.tsx's `validateSearch` house style. */
function validateWithdrawalsSearch(search: Record<string, unknown>): TableSearch<WithdrawalFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    status: typeof search.status === "string" ? (search.status as WithdrawalStatus) : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
  };
}

export const Route = createFileRoute("/_app/withdrawals")({
  validateSearch: validateWithdrawalsSearch,
  component: WithdrawalsPage,
});

// ─── formatting helpers ─────────────────────────────────────────────────────

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-[#6B7280]">{label}</dt>
      <dd className="font-medium text-[#111827]" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </dd>
    </div>
  );
}

const ERROR_BOX = "rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]";

// Only 2 filters here — same "fits directly in the toolbar" reasoning as
// _app.refunds.tsx. Merchant is a plain id text input (not a
// listMerchants()-backed dropdown): that endpoint is MERCHANTS_VIEWER-gated,
// and a FINANCE_VIEWER/CONFIG_MANAGER-only admin managing withdrawals may
// not hold that role — a raw id filter avoids the cross-role dependency.
const SELECT_CLASSNAME = cn(
  "h-9 rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);
const TEXT_INPUT_CLASSNAME = cn(
  "h-9 w-56 rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

// ─── reject dialog ───────────────────────────────────────────────────────────
//
// A regular Dialog (not AlertDialog), mirroring _app.high-risk.tsx's
// RejectDialog closely — but the reason is mandatory here (unlike that
// page's optional note): the submit button stays disabled while the trimmed
// value is empty, so an empty-reason submit can't silently no-op.

interface RejectDialogProps {
  withdrawalId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (data: WithdrawalListItem) => void;
}

function RejectDialog({ withdrawalId, open, onOpenChange, onSuccess }: RejectDialogProps) {
  const [reason, setReason] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setErrorMessage(null);
    }
  }, [open]);

  const trimmedReason = reason.trim();

  const mutation = useMutation({
    mutationFn: () => rejectWithdrawal(withdrawalId, { reason: trimmedReason }),
    onSuccess: (res) => {
      toast.success("Withdrawal rejected.");
      onSuccess(res.data);
    },
    onError: (err) => {
      setErrorMessage(err instanceof ApiError ? err.message : "Failed to reject withdrawal.");
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject this withdrawal?</DialogTitle>
          <DialogDescription>
            The withdrawal becomes REJECTED. The merchant sees the reason you enter below.
          </DialogDescription>
        </DialogHeader>

        {errorMessage && <div className={ERROR_BOX}>{errorMessage}</div>}

        <div className="space-y-1.5">
          <Label htmlFor="withdrawal-reject-reason">Reason (required)</Label>
          <Textarea
            id="withdrawal-reject-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for rejecting this withdrawal…"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || trimmedReason === ""}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reject withdrawal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── withdrawal detail dialog ────────────────────────────────────────────────

interface WithdrawalDetailDialogProps {
  withdrawalId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canManage: boolean;
}

function WithdrawalDetailDialog({ withdrawalId, open, onOpenChange, canManage }: WithdrawalDetailDialogProps) {
  const queryClient = useQueryClient();
  const [withdrawal, setWithdrawal] = useState<WithdrawalListItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);

  useEffect(() => {
    if (!open || !withdrawalId) return;

    let cancelled = false;
    setWithdrawal(null);
    setError(null);
    setLoading(true);

    getWithdrawal(withdrawalId)
      .then((res) => {
        if (!cancelled) setWithdrawal(res.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Failed to load withdrawal.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, withdrawalId]);

  useEffect(() => {
    if (!open) setRejectOpen(false);
  }, [open]);

  const invalidateList = () => queryClient.invalidateQueries({ queryKey: ["withdrawals"] });

  const approveMutation = useMutation({
    mutationFn: () => {
      if (!withdrawal) throw new Error("No withdrawal loaded.");
      return approveWithdrawal(withdrawal.id);
    },
    onSuccess: (res) => {
      toast.success("Withdrawal approved.");
      setWithdrawal(res.data);
      invalidateList();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Failed to approve withdrawal.");
    },
  });

  const handleRejectSuccess = (data: WithdrawalListItem) => {
    setWithdrawal(data);
    setRejectOpen(false);
    invalidateList();
  };

  const isEligible = canManage && withdrawal?.status === WithdrawalStatus.PENDING_REVIEW;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[95vh] max-w-2xl overflow-y-auto">
          {loading && <div className="py-10 text-center text-sm text-[#6B7280]">Loading withdrawal…</div>}
          {error && !loading && <div className="py-10 text-center text-sm text-red-600">{error}</div>}

          {withdrawal && !loading && !error && (
            <>
              <DialogHeader>
                <div className="flex flex-wrap items-center justify-between gap-2 pr-6">
                  <DialogTitle>{withdrawal.merchantBusinessName}</DialogTitle>
                  <StatusBadge
                    label={formatStatusLabel(withdrawal.status)}
                    color={WITHDRAWAL_STATUS_COLOR[withdrawal.status]}
                  />
                </div>
                <DialogDescription asChild>
                  <span className="font-mono text-xs font-semibold">{withdrawal.merchantPublicId}</span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <section className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-4">
                  <div className="mb-4 flex flex-wrap items-end justify-between gap-4 border-b border-[#E5E7EB] pb-4">
                    <Amount native={withdrawal.amount.native} symbol={withdrawal.assetSymbol} usd={withdrawal.amount.usd} />
                    <AssetLabel symbol={withdrawal.assetSymbol} network={withdrawal.assetNetwork} />
                  </div>
                  <dl className="space-y-2">
                    <DetailRow
                      label="Address"
                      value={<HashLink value={withdrawal.address} type="address" network={withdrawal.assetNetwork} />}
                    />
                    <DetailRow label="Destination tag" value={withdrawal.destinationTag ?? "—"} />
                    <DetailRow
                      label="Limit breaches"
                      value={
                        withdrawal.limitBreaches.length === 0 ? (
                          "—"
                        ) : (
                          <div className="flex flex-wrap justify-end gap-1">
                            {withdrawal.limitBreaches.map((breach) => (
                              <StatusBadge key={breach} label={formatStatusLabel(breach)} color="warning" />
                            ))}
                          </div>
                        )
                      }
                    />
                    <DetailRow
                      label="Awaiting liquidity"
                      value={withdrawal.awaitingLiquidity ? "Yes" : "No"}
                    />
                    <DetailRow label="Created" value={formatTimestamp(withdrawal.createdAt)} />
                    {withdrawal.status === WithdrawalStatus.REJECTED && (
                      <DetailRow label="Rejection reason" value={withdrawal.rejectionReason ?? "—"} />
                    )}
                    {withdrawal.payoutTxHash && (
                      <DetailRow
                        label="Payout tx"
                        value={<HashLink value={withdrawal.payoutTxHash} type="tx" network={withdrawal.assetNetwork} />}
                      />
                    )}
                    <DetailRow label="Reviewed at" value={formatTimestamp(withdrawal.reviewedAt)} />
                    <DetailRow label="Approved at" value={formatTimestamp(withdrawal.approvedAt)} />
                  </dl>
                </section>

                {isEligible && (
                  <div className="flex flex-wrap justify-end gap-2 border-t border-[#E5E7EB] pt-4">
                    <Button variant="destructive" onClick={() => setRejectOpen(true)}>
                      Reject
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button disabled={approveMutation.isPending}>
                          {approveMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            "Approve"
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Approve this withdrawal?</AlertDialogTitle>
                          <AlertDialogDescription>
                            The withdrawal becomes APPROVED and moves toward payout. This cannot be
                            undone from the backoffice.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => approveMutation.mutate()}>
                            Approve
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {withdrawal && (
        <RejectDialog
          withdrawalId={withdrawal.id}
          open={rejectOpen}
          onOpenChange={setRejectOpen}
          onSuccess={handleRejectSuccess}
        />
      )}
    </>
  );
}

// ─── row → dialog-opening click ─────────────────────────────────────────────
//
// Same `RowButton` cell-filling pattern as _app.high-risk.tsx's — there's no
// separate route for a withdrawal, just a dialog to open.

function RowButton({ onSelect, children }: { onSelect: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onSelect} className="-m-2 block w-full p-2 text-left">
      {children}
    </button>
  );
}

// ─── withdrawals page ────────────────────────────────────────────────────────

function WithdrawalsPage() {
  const { admin } = Route.useRouteContext();
  const canManage = hasRole(admin.roles, AdminRole.CONFIG_MANAGER);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const tableState = useTableState<WithdrawalFilters>({ defaultFilters: DEFAULT_FILTERS });

  const params: ListWithdrawalsParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    ...tableState.filters,
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["withdrawals", params],
    queryFn: () => listWithdrawals(params),
  });

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  const columns: DataTableColumn<WithdrawalListItem>[] = [
    {
      key: "merchant",
      header: "Merchant",
      cell: (w) => (
        <MerchantCell merchantId={w.merchantId} businessName={w.merchantBusinessName} publicId={w.merchantPublicId} />
      ),
    },
    {
      key: "asset",
      header: "Asset",
      cell: (w) => (
        <RowButton onSelect={() => setSelectedId(w.id)}>
          <AssetLabel symbol={w.assetSymbol} network={w.assetNetwork} />
        </RowButton>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      className: "text-right",
      cell: (w) => (
        <RowButton onSelect={() => setSelectedId(w.id)}>
          <Amount native={w.amount.native} symbol={w.assetSymbol} usd={w.amount.usd} />
        </RowButton>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (w) => (
        <RowButton onSelect={() => setSelectedId(w.id)}>
          <div className="flex flex-wrap items-center gap-1">
            <StatusBadge label={formatStatusLabel(w.status)} color={WITHDRAWAL_STATUS_COLOR[w.status]} />
            {w.limitBreaches.length > 0 && <StatusBadge label="Limit" color="warning" />}
          </div>
        </RowButton>
      ),
    },
    {
      key: "address",
      header: "Address",
      cell: (w) => <HashLink value={w.address} type="address" network={w.assetNetwork} />,
    },
    {
      key: "createdAt",
      header: "Created",
      cell: (w) => (
        <RowButton onSelect={() => setSelectedId(w.id)}>{formatTimestamp(w.createdAt)}</RowButton>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        loading={isPending}
        error={isError ? "Failed to load withdrawals." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        filterBar={
          <>
            <select
              aria-label="Status"
              className={SELECT_CLASSNAME}
              value={tableState.filters.status ?? "ALL"}
              onChange={(e) =>
                tableState.setFilter(
                  "status",
                  e.target.value === "ALL" ? undefined : (e.target.value as WithdrawalStatus),
                )
              }
            >
              <option value="ALL">All statuses</option>
              {Object.values(WithdrawalStatus).map((s) => (
                <option key={s} value={s}>
                  {formatStatusLabel(s)}
                </option>
              ))}
            </select>
            <input
              type="text"
              aria-label="Merchant ID"
              placeholder="Merchant ID"
              className={TEXT_INPUT_CLASSNAME}
              value={tableState.filters.merchantId ?? ""}
              onChange={(e) => tableState.setFilter("merchantId", e.target.value === "" ? undefined : e.target.value)}
            />
            {anyFilterActive && (
              <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
                Clear filters
              </Button>
            )}
          </>
        }
        getRowKey={(w) => w.id}
        emptyMessage="No withdrawals match your filters."
      />

      <WithdrawalDetailDialog
        withdrawalId={selectedId}
        open={selectedId !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
        canManage={canManage}
      />
    </div>
  );
}
